/**
 * ContentTypes.ts
 * Types for gameplay content placed within octile cells.
 * Props (anchor-placed, stateful, <1K faces) vs Decor (auto-scattered, <500 faces).
 */

// ─── Anchor Content ─────────────────────────────────────────

export type AnchorDirection = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'center'

export interface AnchorContent {
    type: 'spawn' | 'trap' | 'loot' | 'prop' | 'interactable'
    id: string
    config: SpawnConfig | TrapConfig | LootConfig | PropConfig
}

// ─── Spawn Config ───────────────────────────────────────────

export type EnemyClass = 'minion' | 'soldier' | 'elite' | 'miniboss' | 'boss'

export interface SpawnConfig {
    id: string
    enemyClass: EnemyClass
    enemyType: string
    anchor: AnchorDirection
    patrolRoute?: AnchorDirection[]
    respawnSeconds: number
    energyCost: number
}

export const SPAWN_ENERGY_COSTS: Record<EnemyClass, number> = {
    minion: 15,
    soldier: 30,
    elite: 60,
    miniboss: 150,
    boss: 500,
}

// ─── Trap Config ────────────────────────────────────────────

export type TrapTrigger = 'pressure_plate' | 'tripwire' | 'proximity' | 'timed' | 'interaction'
export type TrapEffect = 'spike' | 'arrow' | 'pit' | 'poison_gas' | 'boulder' | 'flame_jet' | 'cage' | 'alarm' | 'magical_rune'
export type TrapVisibility = 'obvious' | 'hidden' | 'invisible'

export interface TrapConfig {
    id: string
    trigger: TrapTrigger
    effect: TrapEffect
    visibility: TrapVisibility
    damage: number
    anchor: AnchorDirection
    energyCost: number
}

export const TRAP_ENERGY_COSTS: Record<'simple' | 'moderate' | 'complex', number> = {
    simple: 15,
    moderate: 30,
    complex: 50,
}

// ─── Loot Config ────────────────────────────────────────────

export type ContainerType = 'barrel' | 'crate' | 'sack' | 'chest_wooden' | 'chest_iron' | 'chest_ornate' | 'pedestal' | 'skeleton'
export type LootRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'

export interface LootConfig {
    id: string
    container: ContainerType
    rarity: LootRarity
    anchor: AnchorDirection
    oneTime: boolean
    energyCost: number
}

// ─── Prop Config ────────────────────────────────────────────

export type PropType =
    | 'altar' | 'pedestal' | 'pillar_natural' | 'pillar_carved'
    | 'cage_floor' | 'cage_hanging' | 'spiral_staircase'
    | 'stalagmite_prop' | 'lever' | 'fountain'

export interface PropConfig {
    id: string
    propType: PropType
    anchor: AnchorDirection
    blocksMovement: boolean
    energyCost: number
}

// ─── Building Regulation Limits ─────────────────────────────

export const CELL_LIMITS = {
    maxTrapsPerOctagon: 3,
    maxTrapsPerConnector: 1,
    maxEnemiesPerOctagon: 8,
    maxConsecutiveTrapCells: 2,
    minSafeZoneDistance: 3,       // Max cells between any cell and nearest safe zone
    minBossDistance: 3,            // Min cells between entrance and boss
    minRewardRatio: 0.3,           // 30% of active cells must have loot
    entranceSafeDistance: 1,       // No enemies/traps within 1 cell of entrance
} as const
